/** CurrencyContext — single source of truth for USD↔GHS display.
 *
 *  Backend `/api/fx` returns the live rate (cached 6h). This context fetches
 *  once on mount and exposes:
 *    - `rate`         — current USD→GHS rate (float)
 *    - `fetchedAt`    — ISO timestamp of the last refresh
 *    - `source`       — open.er-api.com | stale-cache | fallback
 *    - `formatGHS(n)` — "GH₵ 1,800" / "GH₵ 15,000" / "Free" for 0
 *    - `formatUSD(n)` — "$159" / "$1,329"  (n in GHS)
 *    - `dual(n)`      — { ghs: "GH₵ 1,800", usd: "$159" }
 *
 *  All app prices live in the database in GHS. USD is purely a display value
 *  derived from the live FX rate. Never compute charge amounts in USD on the
 *  client — Paystack is invoiced in pesewas via /api/payments/initiate.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const FX_FALLBACK = 12.30;
const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [fx, setFx] = useState({ usd_ghs: FX_FALLBACK, fetched_at: null, source: "fallback" });

  useEffect(() => {
    api.get("/fx").then((r) => setFx(r.data)).catch(() => {});
  }, []);

  const value = useMemo(() => {
    const rate = fx.usd_ghs || FX_FALLBACK;
    const formatGHS = (ghs) => {
      if (!ghs || ghs <= 0) return "Free";
      return `GH₵ ${Math.round(ghs).toLocaleString("en-GH")}`;
    };
    const formatUSD = (ghs) => {
      if (!ghs || ghs <= 0) return "";
      return `$${Math.round(ghs / rate).toLocaleString("en-US")}`;
    };
    const dual = (ghs) => ({ ghs: formatGHS(ghs), usd: formatUSD(ghs) });
    return { rate, fetchedAt: fx.fetched_at, source: fx.source, formatGHS, formatUSD, dual };
  }, [fx]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside <CurrencyProvider>");
  return ctx;
}
